import React, { useState } from "react";
import DOMPurify from "dompurify";
import "../css/storyPage.scss";
import { LIST_URL } from "../constans/ApiEndpointConstants";
import { useNavigate } from "react-router-dom";

const UpdateCard = ({ item, resolveUrl }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const showButton = item.text && item.text.length > 100;

  return (
    <div className="story-update-card">
      {item.imgList && item.imgList.length > 0 && (
        <div className="update-image">
          {item.imgList.map((img, idx) => (
            <img key={idx} src={resolveUrl(img)} alt={item.name} />
          ))}
        </div>
      )}
      <div className="update-date">{item.time || "日時不明"}</div>
      <div className="update-title">{item.title}</div>
      <div className={`update-content ${isExpanded ? "expanded" : ""}`}>
        {item.text}
      </div>
      {showButton && (
        <div
          className="update-read-more"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? "閉じる" : "もっと見る"}
        </div>
      )}
    </div>
  );
};

const StoryPage = ({ detailsData, activeTab, setActiveTab, showFullPage }) => {
  // If activeTab/setActiveTab are not provided via props, we could use internal state,
  // but for this refactor we expect them to be passed from CampaignPage.
  // We'll assume they are always passed for now to keep it simple and consistent with the requirement.

  const navigate = useNavigate();

  // 清理空段落（包括只包含 &nbsp; 的 p 标签）和多余的 <br>
  const cleanEmptyParagraphs = (html) => {
    if (!html) return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // 1. 先清理单独的 <br> 标签（在段落开头或结尾的连续 <br>）
    const allElements = tempDiv.querySelectorAll("*");

    allElements.forEach((el) => {
      // 检查元素内是否有连续的 <br> 标签
      const childNodes = Array.from(el.childNodes);

      // 找出连续的 <br> 序列
      let i = 0;
      while (i < childNodes.length) {
        const node = childNodes[i];

        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "BR") {
          // 检查前面是否也是 <br>
          let j = i;
          let brCount = 0;
          while (
            j < childNodes.length &&
            childNodes[j].nodeType === Node.ELEMENT_NODE &&
            childNodes[j].tagName === "BR"
          ) {
            brCount++;
            j++;
          }

          if (brCount > 0) {
            // 删除多余的 <br>，保留一个或全部删除
            // 如果在段落开头或结尾，删除所有
            // 如果在中间，保留一个
            const isAtStart = i === 0;
            const isAtEnd = j >= childNodes.length;

            if (isAtStart || isAtEnd) {
              // 删除所有 <br>
              for (let k = i; k < j; k++) {
                childNodes[k].remove();
              }
            } else {
              // 保留一个 <br>，删除其余的
              for (let k = i + 1; k < j; k++) {
                childNodes[k].remove();
              }
            }
          }
        }

        i++;
      }

      // 2. 清理段落内只有 <br> 和 &nbsp; 的情况
      const textContent = el.textContent.replace(/\u00A0/g, "").trim();
      const hasContent =
        textContent ||
        el.querySelector(
          "img, video, iframe, strong, em, a, span, table, ul, ol, blockquote"
        );

      if (!hasContent && el.tagName === "P") {
        el.remove();
      }
    });

    // 3. 清理顶层连续的 <br>
    const topChildNodes = Array.from(tempDiv.childNodes);
    let topI = 0;
    while (topI < topChildNodes.length) {
      const node = topChildNodes[topI];

      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "BR") {
        let j = topI;
        let brCount = 0;
        while (
          j < topChildNodes.length &&
          topChildNodes[j].nodeType === Node.ELEMENT_NODE &&
          topChildNodes[j].tagName === "BR"
        ) {
          brCount++;
          j++;
        }

        if (brCount > 0) {
          const isAtStart = topI === 0;
          const isAtEnd = j >= topChildNodes.length;

          if (isAtStart || isAtEnd) {
            for (let k = topI; k < j; k++) {
              topChildNodes[k].remove();
            }
          } else {
            for (let k = topI + 1; k < j; k++) {
              topChildNodes[k].remove();
            }
          }
        }
      }
      topI++;
    }

    // 4. 清理空的段落（只包含 &nbsp; 和 <br>）
    const paragraphs = tempDiv.querySelectorAll("p");
    paragraphs.forEach((p) => {
      const clone = p.cloneNode(true);
      const brs = clone.querySelectorAll("br");
      brs.forEach((br) => br.remove());

      const text = clone.textContent.replace(/\u00A0/g, "").trim();
      const hasContent =
        text ||
        clone.querySelector(
          "img, video, iframe, strong, em, a, span, table, ul, ol, blockquote"
        );

      if (!hasContent) {
        p.remove();
      }
    });

    return tempDiv.innerHTML;
  };

  const resolveUrl = (raw) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
    return `${LIST_URL.replace(/\/$/, "")}/${String(raw).replace(/^\//, "")}`;
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSupportClick = (item, index) => {
    const selectedItem = {
      ...item,
      index, // Add index for identification in Checkout
      quantity: 1,
    };

    // Remove heavy lists from detailsData to avoid history state size limits
    const { supportList, updateList, ...lightDetailsData } = detailsData || {};

    navigate("/checkout", {
      state: {
        detailsData: lightDetailsData, // Pass optimized detailsData
        selectedItems: [selectedItem],
      },
    });
  };

  return (
    <div className="story-page-container">
      {/* Tabs */}
      <div className="story-tabs">
        <div
          className={`tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => handleTabClick("overview")}
        >
          プロジェクト概要
          {activeTab === "overview" && <div className="active-bar"></div>}
        </div>
        <div
          className={`tab ${activeTab === "updates" ? "active" : ""}`}
          onClick={() => handleTabClick("updates")}
        >
          活動報告{" "}
          {detailsData?.updateList?.length > 0 && (
            <span className="count">{detailsData?.updateList?.length}</span>
          )}
          {activeTab === "updates" && <div className="active-bar"></div>}
        </div>
        <div
          className={`tab ${activeTab === "comments" ? "active" : ""}`}
          onClick={() => handleTabClick("comments")}
        >
          応援コメント{" "}
          {detailsData?.supportList?.length > 0 && (
            <span className="count">{detailsData?.supportList?.length}</span>
          )}
          {activeTab === "comments" && <div className="active-bar"></div>}
        </div>
      </div>
      <div className="story-tabs-line"></div>

      <div className="story-content-wrapper">
        {/* Left Column: Content based on active tab */}
        <div className="story-main">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <>
              <div className="section-header">
                <span className="icon">
                  <svg
                    className="rfIcon rfIcon-Note css-nkx3mn"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                  >
                    <path
                      className="rfIcon-fill"
                      clipRule="evenodd"
                      d="M5.6 2C4.21444 2 3 3.05666 3 4.55455V19.4455C3 20.9433 4.21444 22 5.6 22H18.4C19.8204 22 21 20.8278 21 19.4455V8.07273C21 7.61033 20.8506 7.18787 20.6159 6.81854L18.6293 3.32398C18.6193 3.30637 18.6088 3.28907 18.5977 3.2721C18.1458 2.57879 17.3843 2 16.4 2H5.6ZM5 4.55455C5 4.25243 5.22556 4 5.6 4H16.4C16.5322 4 16.724 4.07189 16.9077 4.34234L18.8907 7.83057C18.9007 7.84817 18.9112 7.86547 18.9223 7.88244C18.9972 7.99736 19 8.05605 19 8.07273V19.4455C19 19.6995 18.7396 20 18.4 20H5.6C5.22556 20 5 19.7476 5 19.4455V4.55455ZM8.79999 7.60669C8.31674 7.60669 7.92499 7.99844 7.92499 8.48169C7.92499 8.96494 8.31674 9.35669 8.79999 9.35669H15.2C15.6832 9.35669 16.075 8.96494 16.075 8.48169C16.075 7.99844 15.6832 7.60669 15.2 7.60669H8.79999ZM7.92499 11.9182C7.92499 11.435 8.31674 11.0432 8.79999 11.0432H14.4C14.8832 11.0432 15.275 11.435 15.275 11.9182C15.275 12.4015 14.8832 12.7932 14.4 12.7932H8.79999C8.31674 12.7932 7.92499 12.4015 7.92499 11.9182ZM8.79999 14.5613C8.31674 14.5613 7.92499 14.953 7.92499 15.4363C7.92499 15.9195 8.31674 16.3113 8.79999 16.3113H13.6C14.0832 16.3113 14.475 15.9195 14.475 15.4363C14.475 14.953 14.0832 14.5613 13.6 14.5613H8.79999Z"
                      fillRule="evenodd"
                    />
                  </svg>
                </span>
                <h2>プロジェクト本文</h2>
              </div>

              <div
                className="story-body-box ql-editor"
                dangerouslySetInnerHTML={{
                  __html: cleanEmptyParagraphs(
                    DOMPurify.sanitize(
                      (detailsData.post_story || "").replace(/\\"/g, '"')
                    )
                  ),
                }}
              />
            </>
          )}

          {/* Updates Tab */}
          {activeTab === "updates" && (
            <>
              <div className="story-updates-list">
                {detailsData.updateList?.map((item, index) => (
                  <React.Fragment key={index}>
                    <UpdateCard item={item} resolveUrl={resolveUrl} />
                    <hr className="css-1mljwei" />
                  </React.Fragment>
                ))}
              </div>
            </>
          )}

          {/* Comments Tab */}
          {activeTab === "comments" && (
            <>
              <div className="story-comments-list">
                {detailsData.supportList?.map((item, index) => (
                  <div key={index} className="story-comment-card">
                    <div className="comment-top">
                      <div className="comment-avatar">
                        {item.avatar ? (
                          <img src={resolveUrl(item.avatar)} alt="" />
                        ) : (
                          <img src="/main-jp/person1.jpg" alt="" />
                        )}
                      </div>
                      <div className="comment-body">
                        <div className="comment-header">
                          <span className="comment-name">
                            {item.name || "匿名希望"}
                          </span>
                          <span className="comment-date">
                            {item.time || "日時不明"}
                          </span>
                        </div>
                        <div className="comment-content">{item.message}</div>
                      </div>
                    </div>

                    {/* Reply Section (Mocked logic for demo based on image) */}
                    {item.reply && (
                      <div className="comment-reply">
                        <div className="reply-avatar">
                          <img
                            src="https://static.camp-fire.jp/uploads/project_version/image/338579/3081c788-7570-4965-9831-74404987034c.png"
                            alt="Owner"
                          />
                        </div>
                        <div className="reply-body">
                          <div className="reply-name">
                            谷澤雄樹{" "}
                            <span className="reply-to">
                              {item.name || "匿名希望"}さん
                            </span>
                          </div>
                          <div className="reply-text">{item.reply}</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right Column: Returns */}
        {showFullPage && (
          <div className="story-sidebar">
            <div className="section-header">
              <span className="icon">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 12V22H4V12"
                    stroke="#333"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 7H2V12H22V7Z"
                    stroke="#333"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 22V7"
                    stroke="#333"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z"
                    stroke="#333"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z"
                    stroke="#333"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <h2>リターン</h2>
            </div>

            {/* Return Card List */}
            <div className="story-return-card-list">
              {detailsData.donateList?.map((item, index) => (
                <div key={index} className="return-card">
                  <div className="return-header">
                    <span className="amount">
                      {Number(item.price).toLocaleString("en", {
                        maximumFractionDigits: 0,
                      })}{" "}
                      円
                    </span>
                    <span className="fee">+システム利用料</span>
                  </div>
                  <hr className="css-1gfx5i7" />
                  <div className="return-image">
                    <div className="return-image-placeholder">
                      {item.img ? (
                        <img src={resolveUrl(item.img)} alt="" />
                      ) : (
                        <div className="return-image-placeholder-content">
                          <div className="title">お気持ち支援コース</div>
                          <div
                            className="text"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(item.text || ""),
                            }}
                          />
                          <div className="return-image-badge">
                            あり
                            <br />
                            がとう!
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className="return-desc"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.text || ""),
                    }}
                  />

                  <button
                    className="support-btn"
                    onClick={() => handleSupportClick(item, index)}
                  >
                    このリターンで支援する
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryPage;
